import { useQuery } from '@tanstack/react-query'
import { useState, useCallback } from 'react'

import { getWithExtras } from '@/lib/api'

import type { IssueStatus, IssuePriority } from './use-issues'

// ==================== Types ====================

export type MyTaskRole = 'owner' | 'participant' | 'all'

export interface MyTaskItem {
  id: string
  code: string
  title: string
  status: IssueStatus
  priority: IssuePriority
  projectId: string
  projectName: string
  dueDate: string | null
  myRole: 'owner' | 'participant'
  owner: { id: string; name: string } | null
  participantCount: number
}

export interface MyTasksSummary {
  total: number
  byStatus: Record<IssueStatus, number>
  asOwner: number
  asParticipant: number
}

export interface MyTasksQuery {
  role?: MyTaskRole
  status?: IssueStatus[]
  priority?: IssuePriority[]
  projectId?: string
  page?: number
  pageSize?: number
}

export interface MyTasksResponse {
  data: MyTaskItem[]
  meta: {
    page: number
    pageSize: number
    total: number
    totalPages: number
  }
  summary: MyTasksSummary
}

// ==================== Query Keys ====================

export const myTasksKeys = {
  all: ['my-tasks'] as const,
  lists: () => [...myTasksKeys.all, 'list'] as const,
  list: (params: MyTasksQuery) => [...myTasksKeys.lists(), params] as const,
}

// ==================== Hooks ====================

/**
 * 获取我的任务
 */
export function useMyTasks(params: MyTasksQuery = {}) {
  return useQuery({
    queryKey: myTasksKeys.list(params),
    queryFn: async () => {
      const response = await getWithExtras<{
        success: boolean
        data: MyTaskItem[]
        meta: MyTasksResponse['meta']
        summary: MyTasksSummary
      }>('/issues/my-tasks', params as Record<string, unknown>)
      return {
        data: response.data,
        meta: response.meta,
        summary: response.summary,
      }
    },
  })
}

/**
 * 我的任务筛选参数管理
 */
export function useMyTasksFilters(initialParams: MyTasksQuery = {}) {
  const [filters, setFilters] = useState<MyTasksQuery>({
    role: 'all',
    page: 1,
    pageSize: 20,
    ...initialParams,
  })

  const setRole = useCallback((role: MyTaskRole) => {
    setFilters((prev) => ({ ...prev, role, page: 1 }))
  }, [])

  const setStatus = useCallback((status: IssueStatus[] | undefined) => {
    setFilters((prev) => ({ ...prev, status, page: 1 }))
  }, [])

  const setPriority = useCallback((priority: IssuePriority[] | undefined) => {
    setFilters((prev) => ({ ...prev, priority, page: 1 }))
  }, [])

  const setProjectId = useCallback((projectId: string | undefined) => {
    setFilters((prev) => ({ ...prev, projectId, page: 1 }))
  }, [])

  const setPage = useCallback((page: number) => {
    setFilters((prev) => ({ ...prev, page }))
  }, [])

  const setPageSize = useCallback((pageSize: number) => {
    setFilters((prev) => ({ ...prev, pageSize, page: 1 }))
  }, [])

  const resetFilters = useCallback(() => {
    setFilters({
      role: 'all',
      page: 1,
      pageSize: 20,
      ...initialParams,
    })
  }, [initialParams])

  return {
    filters,
    setFilters,
    setRole,
    setStatus,
    setPriority,
    setProjectId,
    setPage,
    setPageSize,
    resetFilters,
  }
}
